import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

const Hero = () => {
  const { language, setLanguage, t } = useLanguage();

  const handleSetLanguage = (lang) => {
    setLanguage(lang);
  };

  return (
    <section className="relative py-20 md:py-28 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 overflow-hidden pattern-bg">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="md:hidden flex justify-center space-x-2 mb-6">
            <Button
              size="sm"
              onClick={() => handleSetLanguage('nl')}
              className={`font-semibold ${language === 'nl' ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              NL
            </Button>
            <Button
              size="sm"
              onClick={() => handleSetLanguage('en')}
              className={`font-semibold ${language === 'en' ? 'bg-primary hover:bg-primary/90 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              EN
            </Button>
          </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="text-center md:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold uppercase tracking-wider text-primary bg-primary/10 rounded-full"
                dangerouslySetInnerHTML={{ __html: t('hero.cuisineType') }}
              />
            </motion.div>

            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight tracking-tight text-gray-800"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t('hero.title')} <span className="brand-text">{t('hero.brandName')}</span>
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-muted-foreground mb-10 max-w-xl mx-auto md:mx-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {t('hero.subtitle')}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Link to="/menu">
                <Button size="lg" className="w-full sm:w-auto primary-button">
                  {t('hero.viewMenu')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact#location">
                <Button variant="outline" size="lg" className="w-full sm:w-auto border-primary text-primary hover:bg-primary/10">
                  <MapPin className="ml-0 mr-2 h-4 w-4" />
                  {t('hero.findUs')}
                </Button>
              </Link>
            </motion.div>
          </div>
          
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5, type: 'spring', stiffness: 100 }}
          >
            <div className="rounded-xl overflow-hidden shadow-2xl border-4 border-white">
              <img src="/images/amsfoodmarket-collage-hero.jpg" className="w-full h-auto" alt={t('hero.subtitle')} />
            </div>
            <div className="absolute -bottom-12 -right-4 md:-bottom-10 md:-right-6 bg-white p-3 rounded-lg shadow-xl border border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-lg font-bold">
                  🌶️
                </div>
                <div>
                  <p className="font-semibold text-gray-700">{t('hero.freshAuthentic')}</p>
                  <p className="text-muted-foreground text-xs">{t('hero.dailyMeals')}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;